import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { RootStore, AddressesStore } from 'AppClient/stores';

import { debounce, isObject, isEqual } from 'lodash-es';
import { Autocomplete, Form, Input } from 'components/antd';
import NotFound from 'components/LocationInput/NotFound';
import { GoogleAddressAndFlightData } from 'app-types';
import { SelectValue } from 'antd/lib/select';
import { getDefaultValue, isDisabled, getFullData } from './flights.func';
import {
	isFlightItem,
	isFlightAddress,
	getPlaceId,
	getPlaceAddress,
	getAirportCorrectedAddress,
	isEnoughLettersForSearch,
} from 'utils/flightsUtils';
import { toDate } from 'utils/converters';
import { Moment } from 'moment';
import { AddressPoints } from 'AppClient/api/Bookings';
import Prefix from './Prefix';
import { getOptions } from './Options';

import classNames from 'classnames';
import styles from './styles.module.scss';

interface Props {
	label?: string;
	searchLabel: string;
	defaultValue?: GoogleAddressAndFlightData | string;
	isPickup?: boolean;
	index: number;
	pickupDateTime?: Moment;
	isDragging: boolean;
	addressesStore?: AddressesStore;
}

interface State {
	currentPlaceId?: string;
	flightNumber?: string;
	inputValue?: string;
	flightSearch?: string;
	isFlight: boolean;

	hiddenFlightNumber?: boolean;
	open?: boolean;
}

@inject(({ addressesStore }: RootStore) => ({
	addressesStore,
}))
@observer
export class FlightAddressInput extends Component<Props, State> {
	isChanged?: boolean;
	canChange = true;
	airportType?: string;
	autocompleteRef = React.createRef<Autocomplete>();

	constructor(props: Props) {
		super(props);
		let inputValue, flightNumber, flightSearch;
		let isFlight = false;
		const { defaultValue, isPickup, searchLabel, index } = props;
		const { airportTypes } = this.props.addressesStore!;

		if (isObject(defaultValue)) {
			inputValue = 'fullAddress' in defaultValue ? defaultValue.fullAddress : '';
			isFlight =
				(isFlightAddress(defaultValue) && defaultValue.flight !== null) ||
				isFlightItem(defaultValue);
			if (
				(isFlightAddress(defaultValue) && isObject(defaultValue.flight)) ||
				isFlightItem(defaultValue)
			) {
				if (isFlightItem(defaultValue)) {
					// editing during booking
					flightNumber = defaultValue.flightNumber;
				} else {
					// editing upcoming/rebooking
					flightNumber = defaultValue.flight
						? defaultValue.flight.flightNumber
						: undefined;
				}

				inputValue = flightNumber;
				const isMiddleLocation = index !== 0 && isPickup;

				flightSearch = isFlightAddress(defaultValue)
					? defaultValue.fullAddress // editing upcoming/rebooking
					: getPlaceAddress(
							defaultValue,
							isPickup,
							isMiddleLocation ? airportTypes[searchLabel] : undefined
					  ); // editing during booking
			}
		}

		this.state = {
			flightNumber,
			flightSearch,
			inputValue,
			isFlight,
			open: false,
			hiddenFlightNumber: false,
		};
	}

	componentDidUpdate(prevProps: Props, _prevState: State) {
		if (
			!isEqual(this.props.defaultValue, prevProps.defaultValue) &&
			this.props.defaultValue === undefined
		) {
			// case with reordering with empty input
			this.setState({
				flightNumber: undefined,
				isFlight: false,
				flightSearch: undefined,
				inputValue: this.state.inputValue,
			});
		}

		if (
			this.props.index !== prevProps.index ||
			this.props.isPickup !== prevProps.isPickup ||
			toDate(this.props.pickupDateTime) !== toDate(prevProps.pickupDateTime)
		) {
			// change order
			// or amount of locations
			// or pickupDateTime
			if (this.state.isFlight) {
				this.resetFlightData(true);

				// reset flights
				this.setState({
					currentPlaceId: undefined,
					flightNumber: undefined,
					isFlight: false,
					flightSearch: undefined,
					inputValue: this.state.flightNumber,
				});
			}
		}
	}

	get date(): string {
		return toDate(this.props.pickupDateTime) as string;
	}

	handleGoogleAddressSearch = (value: SelectValue) => {
		const { receiveGoogleAddresses } = this.props.addressesStore!;
		if (typeof value === 'string') {
			const { searchLabel } = this.props;
			if (value.length === 0 || isEnoughLettersForSearch(value)) {
				this.isChanged = true;
				receiveGoogleAddresses(value as string, {
					label: searchLabel,
					isPickup: this.props.isPickup,
					date: this.date,
				});
				if (!this.state.open && isEnoughLettersForSearch(value)) {
					this.setState({ open: true });
				}
			}
		}
	};

	handleGoogleAddressSearchDebounce = debounce(this.handleGoogleAddressSearch, 1000);

	validateGoogleAddress = (value: SelectValue, location?: GoogleAddressAndFlightData) => {
		const { searchLabel } = this.props;
		const { validateGoogleAddress } = this.props.addressesStore!;

		this.isChanged = false;
		validateGoogleAddress(
			value as string,
			searchLabel,
			undefined,
			undefined,
			location,
			this.airportType
		);
	};

	getPlaceId = (item: GoogleAddressAndFlightData) => {
		const { isPickup } = this.props;

		return getPlaceId(item, isPickup, this.airportType);
	};

	get addressesSuggestions() {
		const { searchLabel } = this.props;
		const { addressesSuggestions } = this.props.addressesStore!;

		return addressesSuggestions[searchLabel];
	}

	onBlur = (value: SelectValue) => {
		const { searchLabel } = this.props;
		const { validateGoogleAddress } = this.props.addressesStore!;
		if (value && this.isChanged) {
			const location = getFullData(this.addressesSuggestions).filter(item => {
				const itemPlaceId = this.getPlaceId(item);
				return itemPlaceId === value;
			})[0];
			validateGoogleAddress(value as string, searchLabel, true, undefined, location);
		}
		this.setState({ open: false });
	};

	addPoint = (addressPoint: AddressPoints | null) => {
		const { index } = this.props;
		const { addAddressPoint } = this.props.addressesStore!;
		addAddressPoint(index, addressPoint);
	};

	onSelect = (value: SelectValue) => {
		const { receiveGoogleAddresses } = this.props.addressesStore!;

		const location = getFullData(this.addressesSuggestions).filter(item => {
			if (isFlightItem(item)) {
				return value === item.departurePlaceId || value === item.arrivalPlaceId;
			} else {
				return value === item.placeId;
			}
		})[0];
		if (!location && !value) {
			this.canChange = true;
			this.setState({
				currentPlaceId: undefined,
				flightNumber: undefined,
				flightSearch: undefined,
				inputValue: undefined,
				isFlight: false,
				open: false,
			});
			this.addPoint(null);
			receiveGoogleAddresses('', { label: this.props.searchLabel });
			return;
		}
		const { isPickup, index } = this.props;
		const isFirstLocation = index === 0;
		this.canChange = false;

		if (isFlightItem(location)) {
			this.setState(
				{
					currentPlaceId: this.getPlaceId(location),
					flightNumber: location.flightNumber,
					flightSearch: getPlaceAddress(location, isPickup, this.airportType),
					isFlight: true,
					hiddenFlightNumber: false,
					open: false,
				},
				() => {
					this.addPoint({
						addressPlaceId: this.getPlaceId(location),
						flightId: location.id,
					});

					this.validateGoogleAddress(
						this.getPlaceId(location),
						isFirstLocation ? location : undefined
					);
					this.airportType = undefined;
				}
			);
		} else {
			this.setState(
				{
					currentPlaceId: undefined,
					inputValue: location.fullAddress,
					flightSearch: undefined,
					isFlight: false,
					open: false,
				},
				() => {
					this.addPoint({
						addressPlaceId: location.placeId,
						flightId: null,
					});
					this.validateGoogleAddress(
						location.placeId,
						isFirstLocation ? location : undefined
					);
				}
			);
		}
	};

	setAirportType = (type: string) => {
		this.airportType = type;
	};

	onChange = (inputValue: SelectValue) => {
		const { resetSingleLocation } = this.props.addressesStore!;

		if (inputValue === undefined && this.state.inputValue) {
			// cleaning input
			resetSingleLocation(this.props.searchLabel, undefined, true);
			this.addPoint(null);
			return this.setState({ inputValue, open: false });
		}
		if (typeof inputValue === 'string') {
			const {
				canChange,
				state: { isFlight },
			} = this;

			if (canChange && !isFlight) {
				this.setState({ inputValue });
			} else {
				this.canChange = true;
			}
		}
	};

	resetFlightData = (needBlur?: boolean) => {
		const { resetSingleLocation } = this.props.addressesStore!;

		const { current } = this.autocompleteRef;
		this.isChanged = true;
		// first reset, then blur
		resetSingleLocation(this.props.searchLabel, undefined, undefined);

		if (needBlur && current) {
			current.blur(this.state.flightNumber);
		}

		if (!needBlur) {
			this.addPoint({
				addressPlaceId: this.state.currentPlaceId || '',
				flightId: null,
			});
		}
	};

	hideFlightNumber = () => {
		const { defaultValue, isPickup, searchLabel } = this.props;
		const { airportTypes } = this.props.addressesStore!;

		// we should take corrected airport address after removing flight
		const inputValue =
			defaultValue &&
			isObject(defaultValue) &&
			(isFlightItem(defaultValue) || isFlightAddress(defaultValue))
				? getAirportCorrectedAddress(defaultValue, isPickup, airportTypes[searchLabel])
				: undefined;

		this.resetFlightData();

		this.setState({
			currentPlaceId: undefined,
			flightNumber: undefined,
			flightSearch: undefined,
			isFlight: false,
			inputValue,
			hiddenFlightNumber: true,
		});

		this.canChange = true;
	};

	getPopupContainer = () => {
		return document.getElementById(`locationAddressInput-${this.props.index}`) || document.body;
	};

	onFocus = () => {
		const { inputValue } = this.state;
		if (inputValue && (!this.addressesSuggestions || this.addressesSuggestions.length === 0)) {
			this.handleGoogleAddressSearch(inputValue);
		}
		if (isEnoughLettersForSearch(inputValue)) {
			this.setState({ open: true });
		}
	};

	get isValid() {
		const { searchLabel } = this.props;
		const { validationAddressErrors } = this.props.addressesStore!;

		return (
			!validationAddressErrors[searchLabel] ||
			validationAddressErrors[searchLabel].length === 0
		);
	}

	render() {
		const {
			props: { label, index, isPickup, pickupDateTime, searchLabel },
			state: { inputValue, open, hiddenFlightNumber, flightNumber, flightSearch, isFlight },
		} = this;
		const { isAddressLoading, isValidateAddressLoading } = this.props.addressesStore!;
		const prefix = flightNumber ? (
			<Prefix
				inputValue={inputValue}
				flightNumber={flightNumber}
				flightSearch={flightSearch}
				resetInputValue={() => {
					this.setState({ inputValue: undefined });
				}}
				hideFlightNumber={this.hideFlightNumber}
			/>
		) : null;
		const notFoundContent = (
			<NotFound
				isAddressLoading={isAddressLoading}
				inputValue={inputValue}
				addressesSuggestions={this.addressesSuggestions}
			/>
		);
		const dataSource = getOptions({
			isPickup,
			index,
			addressesSuggestions: this.addressesSuggestions,
			setAirportType: this.setAirportType,
			getPlaceId: this.getPlaceId,
		});
		return (
			<div
				id={`locationAddressInput-${index}`}
				className={classNames(styles.flightSearchWrapper, {
					[styles.flightSearchIsFlight]: isFlight,
				})}
			>
				<Form.Item label={label}>
					<Autocomplete
						showSearch
						allowClear
						defaultValue={getDefaultValue()}
						optionLabelProp="value"
						dataSource={dataSource}
						value={inputValue}
						disabled={isDisabled(pickupDateTime, isFlight)}
						onChange={this.onChange}
						notFoundContent={notFoundContent}
						filterOption={false}
						getPopupContainer={this.getPopupContainer}
						onSelect={this.onSelect}
						onSearch={this.handleGoogleAddressSearchDebounce}
						onBlur={this.onBlur}
						isValid={this.isValid}
						open={open}
						isLoading={isValidateAddressLoading(searchLabel)}
						ref={this.autocompleteRef}
					>
						<Input
							prefix={prefix}
							onFocus={this.onFocus}
							onFocusWithoutEvent={this.onFocus}
							isFocused={hiddenFlightNumber}
							onKeyDown={({ keyCode }) => {
								const backSpaceKeyCode = 8;
								if (keyCode === backSpaceKeyCode && this.state.isFlight) {
									this.onSelect('');
								}
							}}
						/>
					</Autocomplete>
				</Form.Item>
			</div>
		);
	}
}
